import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";

export default function RetailerLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const nextTarget = params.get("next"); // e.g. "new" -> land on /retailer/new after login
  const isRegister = loc.pathname.includes("register");
  const adminIntent = !isRegister && params.get("as") === "admin";
  const { user, login, register, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const r = isRegister
      ? await register({
          email,
          password,
          name,
          business_name: businessName,
          phone,
          postcode,
        })
      : await login(email, password);
    setLoading(false);
    if (r.ok) {
      // Admins always go to the admin console. Retailers respect ?next=new so
      // the "List your stock" journey lands them on the New Listing form
      // directly, while the header "Retailer sign-in" path lands on the dashboard.
      if (r.user?.role === "admin") {
        nav("/admin");
      } else if (nextTarget === "new") {
        nav("/retailer/new");
      } else {
        nav("/retailer");
      }
    } else setError(r.error);
  }

  function fillDemo(demoEmail, demoPass) {
    setEmail(demoEmail);
    setPassword(demoPass);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 fm-grain" data-testid="retailer-auth-page">
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/vmzgiucw_Roll%20Ends.png"
          alt="Stacks of surplus carpet rolls in an independent flooring warehouse"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Base tint + bottom-heavy gradient so overlaid copy stays legible
            against the busy warehouse image. */}
        <div className="absolute inset-0 bg-slate-900/30" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" aria-hidden="true" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <BrandMark className="w-24 h-24 drop-shadow-lg" />
          </Link>
          <div className="max-w-md">
            <h2 className="font-display font-bold text-4xl leading-tight text-white drop-shadow-lg">
              Turn surplus flooring into sales.
            </h2>
            <p className="mt-3 text-white/90 drop-shadow">
              List roll ends, remnants, offcuts and surplus packs from your phone. No upfront
              listing fees.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-10 flex flex-col justify-center">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 lg:hidden mb-6">
            <BrandMark className="w-20 h-20" />
          </Link>
          <h1 className="font-display font-bold text-3xl text-slate-900">
            {isRegister ? "Set up your retailer account" : adminIntent ? "Admin sign in" : "Sign in to your retailer account"}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {isRegister
              ? "Free to join. Only pay when a remnant sells."
              : adminIntent
                ? "Owner access only. Use your admin email and password."
                : "Manage listings, reservations and sales."}
          </p>

          {adminIntent && user && user.role === "admin" && <Navigate to="/admin" replace />}
          {adminIntent && user && user.role !== "admin" ? (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-slate-700" data-testid="admin-intent-signed-in-notice">
              <p>
                You're signed in as <strong>{user.email}</strong> ({user.role}). Admin access needs the admin
                account, so sign out of this one first.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => logout()} className="fm-btn-primary" data-testid="admin-intent-signout-btn">
                  Sign out and sign in as admin
                </button>
                <Link to={user.role === "retailer" ? "/retailer" : "/"} className="inline-flex items-center px-4 py-2 rounded-full border border-slate-300 bg-white font-semibold text-slate-700 hover:bg-slate-50" data-testid="admin-intent-cancel">
                  Stay signed in
                </Link>
              </div>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="retailer-auth-form">
            {isRegister && (
              <>
                <Field
                  label="Business name"
                  value={businessName}
                  onChange={setBusinessName}
                  required
                  testId="reg-business"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Your name" value={name} onChange={setName} required testId="reg-name" />
                  <Field label="Phone" value={phone} onChange={setPhone} testId="reg-phone" />
                </div>
                <Field
                  label="Shop postcode"
                  value={postcode}
                  onChange={(v) => setPostcode(v.toUpperCase())}
                  testId="reg-postcode"
                />
              </>
            )}
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              required
              testId="auth-email"
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              required
              testId="auth-password"
            />
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="fm-btn-primary w-full disabled:opacity-70"
              data-testid="auth-submit"
            >
              {loading ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
            </button>
          </form>
          )}

          {!isRegister && !adminIntent && (
            <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Try the demo
              </div>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => fillDemo("demo@northernquartercarpets.co.uk", "demo123")}
                  className="text-left px-3 py-2 rounded-md hover:bg-slate-50 transition-colors border border-slate-200"
                  data-testid="demo-retailer-btn"
                >
                  <span className="font-semibold text-slate-900">Northern Quarter Carpets</span>
                  <span className="text-slate-500"> · explore a sample retailer</span>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                A read-only sample shop so you can see the retailer tools. Owner and admin access
                is private.
              </p>
            </div>
          )}

          {!adminIntent && <div className="mt-6 text-sm text-slate-600 text-center">
            {isRegister ? (
              <>
                Already registered?{" "}
                <Link to="/retailer/login" className="font-semibold text-[#E05915]">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                Need an account?{" "}
                <Link to="/retailer/register" className="font-semibold text-[#E05915]">
                  Register
                </Link>
              </>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
        data-testid={testId}
      />
    </div>
  );
}
